import React, { useEffect, useRef, useState } from 'react'

import '../pages/22:06/twenty-two.css'
import { observeActivity, observeSvgActivity } from '../lib/viewport-activity'
import SectionCaption from '../layouts/sectioncaption'
import cards from '../pages/22:06/assets/data/cards.json'
import { ConfessionCard } from '../pages/22:06/card-flow'
import envelopeBeneathWebp from '../pages/22:06/assets/images/envelope-beneath.webp'
import envelopeBeneath from '../pages/22:06/assets/images/envelope-beneath.png'
import envelopeBodyWebp from '../pages/22:06/assets/images/envelope-body.webp'
import envelopeBody from '../pages/22:06/assets/images/envelope-body.png'
import envelopeHeadWebp from '../pages/22:06/assets/images/envelope-head.webp'
import envelopeHead from '../pages/22:06/assets/images/envelope-head.png'
import favicon192 from '../pages/22:06/assets/images/favicon-192.png'

import { BrandLogo } from '../pages/hangzhou/components/shared'
import { initializeSnow } from '../pages/hangzhou/components/effects'
import MapPreview from './footprints-map'
import '../pages/hangzhou/hangzhou.css'
import './home-entries.css'

// Homepage portals share the full 22:06 entry treatment, with palette-only variants.
const PortalEntryLink = ({
  href,
  children,
  tone = 'night',
  className = '',
}) => (
  <a
    className={`PortalEntryLink PortalEntryLink--${tone} ${className}`.trim()}
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={`${children}，在新标签页打开`}
  >
    {children} <i aria-hidden="true">→</i>
  </a>
)

const WARM_WAVES = [
  'm -158 228.759 c 381.046 -411 572.046 -153.25 825.046 -101 s 289 130 457.004 115 c 168 -15 987 245 987 245 h -2269.05 z',
  'm -158 228.759 c 330 -362 548 -210 825.046 -101 s 303 112 471.004 104 c 183 -9 973 255 973 255 h -2269.05 z',
  'm -158 228.759 c 285 -300 520 -275 825.046 -101 s 322 84 490.004 91 c 202 2 954 268 954 268 h -2269.05 z',
]
const COOL_WAVES = [
  'M -240 154 C 120 70 410 126 690 202 C 980 282 1260 54 1840 158 L 1840 420 L -240 420 Z',
  'M -240 190 C 90 142 420 60 760 176 C 1040 272 1330 92 1840 130 L 1840 420 L -240 420 Z',
  'M -240 166 C 130 238 470 76 820 148 C 1130 212 1420 118 1840 184 L 1840 420 L -240 420 Z',
]
const DEEP_WAVES = [
  'M -120 88 C 150 32 365 8 620 68 S 1080 132 1560 72 V 468 H -120 Z',
  'M -120 96 C 190 82 410 2 670 54 S 1120 148 1560 88 V 468 H -120 Z',
  'M -120 82 C 235 120 450 38 735 42 S 1175 112 1560 104 V 468 H -120 Z',
]
const waveValues = (paths) => [...paths, paths[1], paths[0]].join(';')
const Wave = ({ paths, duration, className, fill, viewBox }) => {
  const ref = useRef(null)
  useEffect(() => observeSvgActivity(ref.current), [])
  return (
  <svg
    ref={ref}
    className={className}
    viewBox={viewBox}
    preserveAspectRatio="xMidYMid slice"
    aria-hidden="true"
  >
    <path d={paths[0]} fill={fill}>
      <animate
        attributeName="d"
        dur={duration}
        repeatCount="indefinite"
        calcMode="spline"
        keySplines="0.45 0 0.55 1;0.45 0 0.55 1;0.45 0 0.55 1;0.45 0 0.55 1"
        keyTimes="0;0.25;0.5;0.75;1"
        values={waveValues(paths)}
      />
    </path>
  </svg>
  )
}
const Backdrop = () => (
  <div className="TwentyTwoEntryBackdrop">
    <Wave
      className="TwentyTwoEntryWave TwentyTwoEntryWave--warm"
      paths={WARM_WAVES}
      duration="16s"
      viewBox="0 0 1440 488"
      fill="url(#entry-warm)"
    />
    <Wave
      className="TwentyTwoEntryWave TwentyTwoEntryWave--cool"
      paths={COOL_WAVES}
      duration="22s"
      viewBox="0 0 1600 420"
      fill="url(#entry-cool)"
    />
    <Wave
      className="TwentyTwoEntryWave TwentyTwoEntryWave--deep"
      paths={DEEP_WAVES}
      duration="38s"
      viewBox="0 0 1440 468"
      fill="#1f1f47"
    />
    <svg className="TwentyTwoEntryGradients" aria-hidden="true">
      <defs>
        <linearGradient id="entry-warm" x1="0" y1="0" x2="0" y2="1">
          <stop stopColor="#ff6869" />
          <stop offset=".48" stopColor="#ff927a" />
          <stop offset="1" stopColor="#ff5f8f" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="entry-cool" x1="0" y1="0" x2="0" y2="1">
          <stop stopColor="#8790ec" stopOpacity=".78" />
          <stop offset=".55" stopColor="#b49bdd" stopOpacity=".68" />
          <stop offset="1" stopColor="#d990c2" stopOpacity=".72" />
        </linearGradient>
      </defs>
    </svg>
  </div>
)
const DIGITS = {
  0: ['a', 'b', 'c', 'd', 'e', 'f'],
  2: ['a', 'b', 'd', 'e', 'g'],
  6: ['a', 'c', 'd', 'e', 'f', 'g'],
}
const POINTS = {
  a: '12,1 52,1 57,6 52,11 12,11 7,6',
  b: '58,14 63,19 63,43 58,48 53,43 53,19',
  c: '58,64 63,69 63,93 58,98 53,93 53,69',
  d: '12,101 52,101 57,106 52,111 12,111 7,106',
  e: '6,64 11,69 11,93 6,98 1,93 1,69',
  f: '6,14 11,19 11,43 6,48 1,43 1,19',
  g: '12,51 52,51 57,56 52,61 12,61 7,56',
}
const ENTRY_CARD_IDS = ['card-001', 'card-069']
const ENTRY_CARDS = ENTRY_CARD_IDS.map((cardId) =>
  cards.find((card) => card.id === cardId)
)
const Digit = ({ value }) => (
  <svg className="TwentyTwoEntryDigit" viewBox="0 0 64 112" aria-hidden="true">
    {DIGITS[value].map((segment) => (
      <polygon key={segment} points={POINTS[segment]} />
    ))}
  </svg>
)
const Elevator = () => {
  const ref = useRef(null)
  const [active, setActive] = useState(true)
  useEffect(() => {
    let timer
    const tick = () => {
      setActive(new Date().getSeconds() % 2 === 0)
      timer = window.setTimeout(tick, 1000 - (Date.now() % 1000) + 20)
    }
    const stop = observeActivity(ref.current, visible => {
      window.clearTimeout(timer)
      if (visible) tick()
    })
    return () => { stop(); window.clearTimeout(timer) }
  }, [])
  return (
  <span ref={ref} className="TwentyTwoEntryElevator" role="img" aria-label="22点06分">
    <Digit value={2} />
    <Digit value={2} />
    <span className={`TwentyTwoEntryColon ${active ? 'is-active' : ''}`}>
      <i />
      <i />
    </span>
    <Digit value={0} />
    <Digit value={6} />
  </span>
  )
}
const Envelope = () => (
  <div className="TwentyTwoEntryEnvelope" aria-hidden="true">
    <picture style={{ display: 'contents' }}><source srcSet={envelopeBeneathWebp} type="image/webp" /><img className="TwentyTwoEntryEnvelopeBack" src={envelopeBeneath} alt="" loading="lazy" decoding="async" /></picture>
    <span className="TwentyTwoEntryLetter">
      <b>致每一个浪漫的人</b>
      <i />
      <i />
      <i />
    </span>
    <picture style={{ display: 'contents' }}><source srcSet={envelopeBodyWebp} type="image/webp" /><img className="TwentyTwoEntryEnvelopeBody" src={envelopeBody} alt="" loading="lazy" decoding="async" /></picture>
    <span className="TwentyTwoEntryEnvelopeFlap">
      <picture style={{ display: 'contents' }}><source srcSet={envelopeHeadWebp} type="image/webp" /><img src={envelopeHead} alt="" loading="lazy" decoding="async" /></picture>
      <span className="TwentyTwoEntryEnvelopeSeal">
        <img src={favicon192} alt="" loading="lazy" decoding="async" />
      </span>
    </span>
  </div>
)
const TwentyTwoEntry = () => {
  return (
    <section className="TwentyTwoEntry" aria-label="二十二点零六">
      <div className="TwentyTwoEntryHeading">
        <SectionCaption
          title="二十二点零六"
          emoji="🌙"
          subtitle="每个晚上都会遇见"
        />
      </div>
      <div className="TwentyTwoPortal">
        <Backdrop />
        <div className="TwentyTwoEntryCore">
          <h2>
            <Elevator />
          </h2>
          <PortalEntryLink href="/22:06/">进入心动存档</PortalEntryLink>
        </div>
        <div className="TwentyTwoEntryCards">
          {ENTRY_CARDS.map((card) => (
            <ConfessionCard
              key={card.id}
              card={card}
              expanded={false}
              onToggle={() => {}}
              previewOnly
            />
          ))}
        </div>
        <div className="TwentyTwoEntryEnvelopeWrap">
          <Envelope />
        </div>
      </div>
    </section>
  )
}
// Mountain silhouettes from /hangzhou/'s shoreline, with entry-local colors.
const Mountains = () => (
  <svg className="FootprintsMountains" viewBox="0 0 360 230" aria-hidden="true">
    <path
      fill="#9ac6cc"
      opacity=".42"
      d="M-20 157C4 155 10 133 28 125C44 118 46 98 57 90C68 81 71 58 84 55C95 52 98 71 109 80C119 88 128 90 140 108C152 126 156 160 174 160C194 160 214 128 231 125C245 122 254 138 266 150C279 162 292 165 307 168C326 173 338 162 352 166C365 170 374 179 386 183V240H-20Z"
    />
    <path
      fill="#397f74"
      opacity=".15"
      d="M-20 197C1 180 12 174 28 155C40 141 49 144 59 127C69 111 76 113 84 94C89 83 91 74 99 73C109 71 113 89 121 98C130 109 137 109 145 124C154 141 166 177 178 170C190 162 203 151 215 147C226 143 233 140 241 145C253 151 259 163 274 174C290 186 303 181 319 185C339 190 352 193 367 201L386 214V240H-20Z"
    />
    <path
      fill="#397f74"
      opacity=".12"
      d="M-20 216C-1 209 12 196 27 193C44 189 43 179 56 176C70 172 82 190 96 192C115 195 119 180 132 177C145 174 147 158 159 157C173 155 179 175 191 181C206 188 216 191 230 183C242 177 249 163 261 161C275 158 282 177 296 180C310 184 316 176 327 182C343 190 350 188 363 199C371 206 378 206 386 208V240H-20Z"
    />
  </svg>
)

const FootprintsEntry = () => {
  const root = useRef(null)
  useEffect(() => {
    const stopSnow = initializeSnow(root.current, {
      updateFavicon: false,
      softSnow: true,
      startBare: true,
    })
    return () => {
      if (stopSnow) stopSnow()
    }
  }, [])
  return (
    <section
      ref={root}
      className="FootprintsEntry"
      id="footprints"
      aria-label="杭州足迹"
    >
      <SectionCaption
        title="杭州足迹"
        emoji="👣"
        subtitle="杭州安静的下起了雪"
      />
      <div className="FootprintsPortal HangzhouPreview">
        <Mountains />
        <div className="FootprintsStory">
          <button
            className="FootprintsSnow brand-weather"
            type="button"
            disabled
            aria-label="暂停落雪"
            aria-pressed="false"
            title="暂停落雪"
          >
            <BrandLogo idPrefix="footprints-brand" />
          </button>
          <PortalEntryLink
            href="/hangzhou/"
            tone="lake"
            className="FootprintsOpen"
          >
            进入足迹地图
          </PortalEntryLink>
        </div>
        <div className="FootprintsAtlas" aria-hidden="true">
          <MapPreview />
        </div>
      </div>
    </section>
  )
}

const MemoTwentyTwoEntry = React.memo(TwentyTwoEntry)
const MemoFootprintsEntry = React.memo(FootprintsEntry)

export { MemoTwentyTwoEntry as TwentyTwoEntry, MemoFootprintsEntry as FootprintsEntry }
